import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const NotificationPanel = ({ notifications = [] }) => {
  const [filter, setFilter] = useState('all');

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'rfq': return 'FileText';
      case 'payment': return 'CreditCard';
      case 'system': return 'Settings';
      case 'customer': return 'User';
      case 'order': return 'ShoppingBag';
      default: return 'Bell';
    }
  };

  const getNotificationColor = (priority) => {
    switch (priority) {
      case 'high': return 'border-l-error bg-error/5';
      case 'medium': return 'border-l-warning bg-warning/5';
      case 'low': return 'border-l-success bg-success/5';
      default: return 'border-l-primary bg-primary/5';
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const diff = now - new Date(timestamp);
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    
    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return 'Just now';
  };

  const filteredNotifications = filter === 'all' 
    ? notifications 
    : notifications?.filter(n => n?.type === filter);

  const filterOptions = [
    { value: 'all', label: 'All', count: notifications?.length },
    { value: 'rfq', label: 'RFQs', count: notifications?.filter(n => n?.type === 'rfq')?.length },
    { value: 'payment', label: 'Payments', count: notifications?.filter(n => n?.type === 'payment')?.length },
    { value: 'system', label: 'System', count: notifications?.filter(n => n?.type === 'system')?.length }
  ];

  return (
    <div className="bg-card border border-border rounded-lg shadow-subtle">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-foreground">Real-time Notifications</h2>
          <Button variant="ghost" size="sm">
            <Icon name="Settings" size={16} />
            Settings
          </Button>
        </div>

        <div className="flex items-center space-x-2">
          {filterOptions?.map((option) => (
            <button
              key={option?.value}
              onClick={() => setFilter(option?.value)}
              className={`px-3 py-1 text-xs font-medium rounded-full transition-smooth ${
                filter === option?.value
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:text-foreground'
              }`}
            >
              {option?.label} ({option?.count})
            </button>
          ))}
        </div>
      </div>
      <div className="max-h-96 overflow-y-auto">
        {filteredNotifications?.length > 0 ? (
          <div className="divide-y divide-border">
            {filteredNotifications?.map((notification) => (
              <div
                key={notification?.id}
                className={`p-4 border-l-4 hover:bg-muted/50 transition-smooth ${getNotificationColor(notification?.priority)}`}
              >
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 mt-0.5">
                    <div className="w-8 h-8 bg-background rounded-full flex items-center justify-center">
                      <Icon name={getNotificationIcon(notification?.type)} size={16} className="text-foreground" />
                    </div>
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm font-medium text-foreground truncate">
                        {notification?.title}
                      </p>
                      <span className="text-xs text-muted-foreground flex-shrink-0 ml-2">
                        {formatTimeAgo(notification?.timestamp)}
                      </span>
                    </div>
                    
                    <p className="text-sm text-muted-foreground mb-2">
                      {notification?.message}
                    </p>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          notification?.priority === 'high' ? 'bg-error/10 text-error' :
                          notification?.priority === 'medium'? 'bg-warning/10 text-warning' : 'bg-success/10 text-success'
                        }`}>
                          {notification?.priority?.toUpperCase()}
                        </span>
                        <span className="text-xs text-muted-foreground capitalize">
                          {notification?.type}
                        </span>
                      </div>
                      
                      {notification?.actionUrl && (
                        <Button variant="ghost" size="sm">
                          <Icon name="ExternalLink" size={12} />
                          View
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-12 text-center">
            <Icon name="Bell" size={48} className="mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">No Notifications</h3>
            <p className="text-muted-foreground">
              {filter === 'all' ? 'All caught up! No new notifications.' : `No ${filter} notifications at the moment.`}
            </p>
          </div>
        )}
      </div>
      {filteredNotifications?.length > 0 && (
        <div className="p-4 border-t border-border">
          <Button variant="ghost" size="sm" fullWidth>
            Mark All as Read
          </Button>
        </div>
      )}
    </div>
  );
};

export default NotificationPanel;